<template>
  <component :is="component" v-if="component" :size="size" :stroke-width="strokeWidth" class="lucide-icon"/>
</template>
<script setup lang="ts">
import type {Component} from "vue";
import {
  ArrowRight, Bath, Bed, BrickWall, Camera, Car, Check, ChefHat, ChevronDown,
  ChevronLeft, Circle, CircleAlert, CircleCheck, CirclePlus, CircleQuestionMark, Clock, DoorClosed, DoorOpen, DoorStairwell,
  Download, Eye, EyeOff, FileText, FolderOpen, Folders, Hammer, HardHat, Heater, Hourglass, Image, Info,
  Languages, LoaderCircle, Mailbox, Menu, Navigation, Pen, Phone, Settings, ShoppingCart, Sofa, Square,
  SquareCheck, Tags, Tent, Toilet, Trash2, TriangleAlert, UserRound, Utensils, WashingMachine, Wine, Wrench, X,
} from "@lucide/vue";

/**
 * Only the icons registered here are bundled. Names follow Lucide's kebab-case naming.
 */
const icons: Record<string, Component> = {
  'arrow-right': ArrowRight,
  'bath': Bath,
  'bed': Bed,
  'brick-wall': BrickWall,
  'camera': Camera,
  'car': Car,
  'check': Check,
  'chef-hat': ChefHat,
  'chevron-down': ChevronDown,
  'chevron-left': ChevronLeft,
  'circle': Circle,
  'circle-alert': CircleAlert,
  'circle-check': CircleCheck,
  'circle-plus': CirclePlus,
  'circle-question-mark': CircleQuestionMark,
  'clock': Clock,
  'door-closed': DoorClosed,
  'door-open': DoorOpen,
  'door-stairwell': DoorStairwell,
  'download': Download,
  'eye': Eye,
  'eye-off': EyeOff,
  'file-text': FileText,
  'folder-open': FolderOpen,
  'folders': Folders,
  'hammer': Hammer,
  'hard-hat': HardHat,
  'heater': Heater,
  'hourglass': Hourglass,
  'image': Image,
  'info': Info,
  'languages': Languages,
  'loader-circle': LoaderCircle,
  'mailbox': Mailbox,
  'menu': Menu,
  'navigation': Navigation,
  'pen': Pen,
  'phone': Phone,
  'settings': Settings,
  'shopping-cart': ShoppingCart,
  'sofa': Sofa,
  'square': Square,
  'square-check': SquareCheck,
  'tags': Tags,
  'tent': Tent,
  'toilet': Toilet,
  'trash': Trash2,
  'triangle-alert': TriangleAlert,
  'user-round': UserRound,
  'utensils': Utensils,
  'washing-machine': WashingMachine,
  'wine': Wine,
  'wrench': Wrench,
  'x': X,
}

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  // Defaults to 1em so the icon scales with font-size (text-xl, text-6xl...), like the former icon font
  size: {
    type: [String, Number],
    default: '1em',
  },
  strokeWidth: {
    type: [String, Number],
    default: 1.5,
  },
})

const component = computed(() => {
  if (import.meta.dev && !icons[props.name]) {
    console.warn(`[Icon] Unknown icon "${props.name}", register it in components/Icon.vue`)
  }
  return icons[props.name]
})
</script>
<style scoped>
.lucide-icon {
  display: inline-block;
  flex-shrink: 0;
  vertical-align: -0.125em;
}
</style>
